import React, { useEffect, useMemo, useState, useCallback } from "react";
import Navbar from "../Navbar/Navbar.jsx";
import TopBar from "../Navbar/Mainbar.jsx";

/**
 * Tickets — issue-escalation console (Technical -> Network, Salary -> Finance).
 * Employees and admins raise tickets; assignees resolve them; superadmin is
 * CC'd and gets the SLA reminder. Mounts in the portal admin shell, 24h time.
 */

const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === "production") return "https://api.leave-management.manurevasolutions.com";
  return "http://localhost:5000";
};
const BASE = getBackendUrl();
const API = `${BASE}/api/tickets`;

const PRIORITY = {
  high:   { label: "High",   color: "#b91c1c", bg: "#fee2e2", rail: "#ef4444", sla: "Response within 1 hour" },
  medium: { label: "Medium", color: "#b45309", bg: "#fef3c7", rail: "#f59e0b", sla: "Response within half a day" },
  low:    { label: "Low",    color: "#1d4ed8", bg: "#dbeafe", rail: "#3b82f6", sla: "Response within one day" },
};
const STATUS = {
  open:        { label: "Open",        color: "#b45309", bg: "#fef3c7" },
  in_progress: { label: "In progress", color: "#1d4ed8", bg: "#dbeafe" },
  resolved:    { label: "Resolved",    color: "#15803d", bg: "#dcfce7" },
  closed:      { label: "Closed",      color: "#4b5563", bg: "#e5e7eb" },
};
const STATUS_ORDER = ["open", "in_progress", "resolved", "closed"];

/* ---------- api helpers ---------- */
async function apiGet(path) {
  const r = await fetch(API + path, { credentials: "include" });
  if (r.status === 403) throw { forbidden: true };
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(API + path, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (r.status === 403) throw { forbidden: true };
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}
async function apiPostForm(path, formData) {
  const r = await fetch(API + path, { method: "POST", credentials: "include", body: formData });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}

const Swal = () => (typeof window !== "undefined" ? window.Swal : null);
function toast(msg, ok = true) {
  const s = Swal();
  if (s) s.fire({ toast: true, position: "top-end", timer: 2400, showConfirmButton: false,
    icon: ok ? "success" : "error", title: msg });
  else if (!ok) alert(msg);
}

/* ---------- small components ---------- */
function Pill({ map, k }) {
  const s = map[k] || { label: k, color: "#4b5563", bg: "#e5e7eb" };
  return <span className="tk-pill" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
}

function Forbidden() {
  return (
    <div className="tk-card tk-forbidden">
      <h3>Superadmin only</h3>
      <p>The full ticket list is limited to Superadmins. You can still see tickets you
        raised, and ones assigned to you.</p>
    </div>
  );
}

/* ===================== Tab 1 — Raise a ticket ===================== */
function RaiseTicket({ onRaised }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("medium");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]);       // File[]
  const [docs, setDocs] = useState([]);           // File[]
  const [loadingA, setLoadingA] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiGet("/categories").then((r) => setCategories(r.categories || []))
      .catch((e) => toast(e.message || "Couldn't load categories", false));
  }, []);

  const onCategory = useCallback(async (val) => {
    setCategory(val); setAssignee(""); setAssignees([]);
    if (!val) return;
    setLoadingA(true);
    try {
      const r = await apiGet(`/assignees?category=${encodeURIComponent(val)}`);
      setAssignees(r.assignees || []);
      if ((r.assignees || []).length === 0)
        toast(`No one is set up on the ${r.team} team yet`, false);
    } catch (e) { toast(e.message || "Couldn't load people", false); }
    finally { setLoadingA(false); }
  }, []);

  const addFiles = (setter, existing) => (e) => {
    const picked = Array.from(e.target.files || []);
    setter([...existing, ...picked]);
    e.target.value = ""; // allow re-picking the same file
  };
  const removeAt = (setter, arr, i) => () => setter(arr.filter((_, idx) => idx !== i));

  const reset = () => {
    setCategory(""); setAssignees([]); setAssignee("");
    setPriority("medium"); setMessage(""); setImages([]); setDocs([]);
  };

  const submit = async () => {
    if (!category)  return toast("Pick a category", false);
    if (!assignee)  return toast("Choose who to assign this to", false);
    if (!message.trim()) return toast("Describe the issue", false);

    const fd = new FormData();
    fd.append("category", category);
    fd.append("priority", priority);
    fd.append("assignee_id", assignee);
    fd.append("message", message.trim());
    images.forEach((f) => fd.append("images", f));
    docs.forEach((f) => fd.append("documents", f));

    setSubmitting(true);
    try {
      const r = await apiPostForm("", fd);
      toast(`Ticket #${r.ticket?.id} raised`);
      reset();
      onRaised?.();
    } catch (e) { toast(e.message || "Couldn't raise the ticket", false); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="tk-formwrap">
      <div className="tk-card tk-form">
        <label className="tk-label">What kind of issue?</label>
        <select className="tk-input" value={category} onChange={(e) => onCategory(e.target.value)}>
          <option value="">Select a category…</option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>{c.label} → {c.team} team</option>
          ))}
        </select>

        <label className="tk-label">Assign to</label>
        <select className="tk-input" value={assignee} disabled={!category || loadingA}
          onChange={(e) => setAssignee(e.target.value)}>
          <option value="">{loadingA ? "Loading…" : "Select a person…"}</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{a.employee_code ? ` (${a.employee_code})` : ""}
            </option>
          ))}
        </select>

        <label className="tk-label">Priority</label>
        <div className="tk-prio">
          {["high", "medium", "low"].map((p) => {
            const s = PRIORITY[p];
            return (
              <button key={p} type="button" onClick={() => setPriority(p)}
                className={`tk-prio__opt ${priority === p ? "is-on" : ""}`}
                style={{ "--rail": s.rail }}>
                <span className="tk-prio__name" style={{ color: s.color }}>{s.label}</span>
                <span className="tk-prio__sla">{s.sla}</span>
              </button>
            );
          })}
        </div>

        <label className="tk-label">Describe the issue</label>
        <textarea className="tk-input tk-textarea" rows={5} value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What happened, and what do you need?" />

        <div className="tk-uploads">
          <div className="tk-upload">
            <label className="tk-label">Screenshots (optional)</label>
            <label className="tk-filebtn">
              + Add images
              <input type="file" accept="image/*" multiple hidden onChange={addFiles(setImages, images)} />
            </label>
            {images.length > 0 && (
              <div className="tk-thumbs">
                {images.map((f, i) => (
                  <div key={i} className="tk-thumb">
                    <img alt={f.name} src={URL.createObjectURL(f)} />
                    <button type="button" className="tk-thumb__x" onClick={removeAt(setImages, images, i)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="tk-upload">
            <label className="tk-label">Documents (optional)</label>
            <label className="tk-filebtn">
              + Add documents
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                multiple hidden onChange={addFiles(setDocs, docs)} />
            </label>
            {docs.length > 0 && (
              <ul className="tk-filelist">
                {docs.map((f, i) => (
                  <li key={i}>
                    <span>📄 {f.name}</span>
                    <button type="button" onClick={removeAt(setDocs, docs, i)}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="tk-actions">
          <button className="tk-btn tk-btn--primary" disabled={submitting} onClick={submit}>
            {submitting ? "Raising…" : "Raise ticket"}
          </button>
          <button className="tk-btn" disabled={submitting} onClick={reset}>Clear</button>
        </div>
       
      </div>
    </div>
  );
}

/* ===================== Tabs 2–4 — ticket lists ===================== */
function TicketTable({ scope, personCol, editable, onForbidden, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiGet(`?scope=${scope}`)); }
    catch (e) {
      if (e.forbidden) return onForbidden?.();
      toast(e.message || "Couldn't load tickets", false);
    } finally { setLoading(false); }
  }, [scope, onForbidden]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (t, status) => {
    setBusy(t.id);
    try {
      await apiPost(`/${t.id}/status`, { status });
      toast(`Ticket #${t.id} → ${STATUS[status]?.label || status}`);
      await load();
    } catch (e) { toast(e.message || "Couldn't update status", false); }
    finally { setBusy(null); }
  };

  if (loading && !data) return <p className="tk-muted tk-pad">Loading…</p>;
  const rows = data?.tickets || [];

  if (rows.length === 0)
    return (
      <div className="tk-empty">
        <div className="tk-empty__mark">✓</div>
        <p><strong>Nothing here.</strong> {scope === "assigned"
          ? "No tickets are assigned to you." : "No tickets yet."}</p>
      </div>
    );

  return (
    <div className="tk-card tk-tablecard">
      <table className="tk-table">
        <thead>
          <tr>
            <th style={{ width: 54 }}>#</th>
            <th>Category</th>
            <th>Priority</th>
            <th>Status</th>
            <th>{personCol}</th>
            <th>Raised</th>
            <th>SLA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const isOpen = !!open[t.id];
            const person = personCol === "Assignee" ? t.assignee_name : t.raised_by_name;
            return (
              <React.Fragment key={t.id}>
                <tr className={t.overdue ? "is-overdue" : ""} onClick={() => setOpen((o) => ({ ...o, [t.id]: !o[t.id] }))}>
                  <td className="tk-idcell">#{t.id}<span className="tk-chev">{isOpen ? "▾" : "▸"}</span></td>
                  <td data-label="Category">{t.category_label}</td>
                  <td data-label="Priority"><Pill map={PRIORITY} k={t.priority} /></td>
                  <td data-label="Status"><Pill map={STATUS} k={t.status} /></td>
                  <td data-label={personCol} className="tk-name">{person || "—"}</td>
                  <td data-label="Raised" className="tk-muted">{(t.created_at || "").slice(0, 10)}</td>
                  <td data-label="SLA">
                    {t.status !== "open"
                      ? <span className="tk-muted">—</span>
                      : t.overdue
                        ? <span className="tk-slabad">Overdue</span>
                        : <span className="tk-muted">{Math.max(0, Math.round((t.sla_minutes - (t.elapsed_min || 0)) / 6) / 10)}h left</span>}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="tk-detailrow">
                    <td colSpan={7}>
                      <div className="tk-detail">
                        <p className="tk-msg">{t.message}</p>

                        {(t.attachments || []).length > 0 && (
                          <div className="tk-att">
                            {t.attachments.map((a) => (
                              <a key={a.id} className={`tk-attchip tk-attchip--${a.kind}`}
                                href={`${API}/${t.id}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}>
                                {a.kind === "image" ? "🖼" : "📄"} {a.original_name || a.filename}
                              </a>
                            ))}
                          </div>
                        )}

                        <div className="tk-detail__meta">
                          <span>Raised by <strong>{t.raised_by_name || "—"}</strong></span>
                          <span>Assigned to <strong>{t.assignee_name || "—"}</strong></span>
                          {t.team && <span>{t.team} team</span>}
                        </div>

                        {editable && (
                          <div className="tk-statusbar" onClick={(e) => e.stopPropagation()}>
                            <span className="tk-label" style={{ margin: 0 }}>Set status</span>
                            {STATUS_ORDER.map((s) => (
                              <button key={s} disabled={busy === t.id || t.status === s}
                                className={`tk-btn tk-btn--sm ${t.status === s ? "is-current" : ""}`}
                                onClick={() => setStatus(t, s)}>
                                {STATUS[s].label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ===================== Shell ===================== */
export default function Tickets() {
  const [tab, setTab] = useState("raise");
  const [forbidden, setForbidden] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const onForbidden = useCallback(() => setForbidden(true), []);

  const tabs = [
    { key: "raise",    label: "Raise a ticket" },
    { key: "mine",     label: "My tickets" },
    { key: "assigned", label: "Assigned to me" },
    { key: "all",      label: "Update Ticket Status" },
  ];

  const bump = () => { setRefreshKey((k) => k + 1); setTab("mine"); };
  const pick = (k) => { setForbidden(false); setTab(k); };

  return (
    <>
      <div className="Admin-auditlogs-container">
        <TopBar />
        <div style={{ display: "flex", minHeight: "100vh", background: "#f0f2f5" }}>
          <div className="mdmanager-navbar" style={{ overflowX: "hidden" }}><Navbar /></div>
          <div className="tk-root" style={{ flex: 1, minWidth: 0, padding: 24 }}>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
            <style>{CSS}</style>

            <div className="tk-wrap">
              <h2 className="tk-h1">Support tickets</h2>
              <div className="tk-tabs" role="tablist">
                {tabs.map((t) => (
                  <button key={t.key} role="tab" aria-selected={tab === t.key}
                    className={`tk-tab ${tab === t.key ? "is-on" : ""}`} onClick={() => pick(t.key)}>
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "raise" && <RaiseTicket onRaised={bump} />}
              {tab === "mine" && (
                <TicketTable scope="mine" personCol="Assignee" editable={false}
                  refreshKey={refreshKey} />
              )}
              {tab === "assigned" && (
                <TicketTable scope="assigned" personCol="Raised by" editable={true}
                  refreshKey={refreshKey} />
              )}
              {tab === "all" && (forbidden
                ? <Forbidden />
                : <TicketTable scope="all" personCol="Assignee" editable={true}
                    onForbidden={onForbidden} refreshKey={refreshKey} />)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const CSS = `
.tk-root{
  --navy:#101F40; --blue:#2563eb; --ink:#1f2937; --sub:#6b7280;
  --line:#e8e4df; --line-2:#f0f2f5; --card:#fff;
  font-family:'DM Sans',system-ui,sans-serif; color:var(--ink);
}
.tk-root *{box-sizing:border-box}
.tk-wrap{max-width:1080px; margin:0 auto; padding:4px}
.tk-h1{font-size:22px; font-weight:700; color:var(--navy); margin:0 0 16px}
.tk-pad{padding:20px}
.tk-muted{color:var(--sub); font-size:13px}
.tk-note{font-size:12px; color:var(--sub); margin:12px 0 0}
.tk-tabs{display:flex; gap:2px; border-bottom:1px solid var(--line); margin-bottom:20px; overflow-x:auto}
.tk-tabs::-webkit-scrollbar{display:none}
.tk-tab{appearance:none; border:0; background:none; padding:10px 16px; font:600 14px 'DM Sans';
  color:var(--sub); cursor:pointer; white-space:nowrap; border-bottom:2px solid transparent; margin-bottom:-1px}
.tk-tab:hover{color:var(--ink)}
.tk-tab.is-on{color:var(--blue); border-bottom-color:var(--blue); font-weight:700}
.tk-card{background:var(--card); border:1px solid var(--line); border-radius:16px}

/* form */
.tk-formwrap{max-width:640px}
.tk-form{padding:22px}
.tk-label{display:block; font:600 12px 'DM Sans'; text-transform:uppercase; letter-spacing:.04em;
  color:var(--sub); margin:16px 0 7px}
.tk-form .tk-label:first-child{margin-top:0}
.tk-input{width:100%; padding:10px 12px; border:1px solid var(--line); border-radius:10px;
  font:500 14px 'DM Sans'; color:var(--ink); background:#fff}
.tk-input:focus{outline:none; border-color:var(--blue); box-shadow:0 0 0 3px #2563eb22}
.tk-input:disabled{background:#f7f8fa; color:var(--sub)}
.tk-textarea{resize:vertical; line-height:1.5}
.tk-prio{display:grid; grid-template-columns:repeat(3,1fr); gap:10px}
.tk-prio__opt{appearance:none; cursor:pointer; text-align:left; background:#fff;
  border:1px solid var(--line); border-radius:12px; padding:12px 14px; position:relative; overflow:hidden}
.tk-prio__opt::before{content:""; position:absolute; left:0; top:10px; bottom:10px; width:3px;
  border-radius:3px; background:var(--rail)}
.tk-prio__opt.is-on{border-color:var(--navy); box-shadow:inset 0 0 0 1px var(--navy)}
.tk-prio__name{display:block; font-weight:700; font-size:15px}
.tk-prio__sla{display:block; font-size:11px; color:var(--sub); margin-top:2px}
.tk-uploads{display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-top:6px}
.tk-filebtn{display:inline-block; cursor:pointer; font:600 13px 'DM Sans'; color:var(--blue);
  border:1px dashed #b9c6e6; background:#f5f8ff; padding:9px 14px; border-radius:10px}
.tk-filebtn:hover{background:#eef3ff}
.tk-thumbs{display:flex; flex-wrap:wrap; gap:8px; margin-top:10px}
.tk-thumb{position:relative; width:64px; height:64px; border-radius:10px; overflow:hidden; border:1px solid var(--line)}
.tk-thumb img{width:100%; height:100%; object-fit:cover; display:block}
.tk-thumb__x{position:absolute; top:2px; right:2px; width:18px; height:18px; border:0; border-radius:50%;
  background:#000a; color:#fff; cursor:pointer; font-size:12px; line-height:1}
.tk-filelist{list-style:none; margin:10px 0 0; padding:0; display:flex; flex-direction:column; gap:6px}
.tk-filelist li{display:flex; justify-content:space-between; align-items:center; font-size:13px;
  background:#f7f8fa; border:1px solid var(--line); border-radius:9px; padding:7px 11px}
.tk-filelist button{border:0; background:none; color:#c0392b; cursor:pointer; font:600 12px 'DM Sans'}
.tk-actions{display:flex; gap:10px; margin-top:22px}
.tk-btn{appearance:none; cursor:pointer; font:600 13px 'DM Sans'; padding:10px 16px; border-radius:10px;
  border:1px solid var(--line); background:#fff; color:var(--ink); transition:.12s}
.tk-btn:hover{border-color:#c9cdd3}
.tk-btn:disabled{opacity:.55; cursor:default}
.tk-btn--primary{background:var(--navy); border-color:var(--navy); color:#fff}
.tk-btn--primary:hover{filter:brightness(1.08)}
.tk-btn--sm{padding:6px 12px; font-size:12px}
.tk-btn.is-current{background:var(--navy); border-color:var(--navy); color:#fff}

/* tables */
.tk-tablecard{overflow:hidden}
.tk-table{width:100%; border-collapse:collapse}
.tk-table th{text-align:left; padding:11px 16px; background:var(--navy); color:#fff;
  font:700 11px 'DM Sans'; text-transform:uppercase; letter-spacing:.05em; white-space:nowrap}
.tk-table td{padding:12px 16px; border-bottom:1px solid var(--line-2); font-size:14px; vertical-align:middle}
.tk-table tbody tr{cursor:pointer}
.tk-table tbody tr:hover{background:#f7f8fa}
.tk-table tbody tr.is-overdue{box-shadow:inset 3px 0 0 #ef4444; background:#fff7f6}
.tk-name{font-weight:600}
.tk-idcell{font-weight:700; color:var(--navy); white-space:nowrap}
.tk-chev{color:var(--sub); font-size:12px; margin-left:6px}
.tk-pill{display:inline-block; padding:3px 10px; border-radius:999px; font-size:12px; font-weight:600; white-space:nowrap}
.tk-slabad{color:#b91c1c; background:#fee2e2; font-size:12px; font-weight:700; padding:3px 10px; border-radius:999px}
.tk-detailrow > td{background:#fafbfc; padding:14px 16px}
.tk-detail{display:flex; flex-direction:column; gap:12px}
.tk-msg{margin:0; font-size:14px; line-height:1.55; white-space:pre-wrap}
.tk-att{display:flex; flex-wrap:wrap; gap:8px}
.tk-attchip{display:inline-flex; align-items:center; gap:6px; text-decoration:none; font-size:12.5px;
  font-weight:600; color:var(--ink); background:#eef2f7; border:1px solid var(--line); padding:6px 11px; border-radius:9px}
.tk-attchip:hover{background:#e3e9f2}
.tk-detail__meta{display:flex; flex-wrap:wrap; gap:16px; font-size:12.5px; color:var(--sub)}
.tk-detail__meta strong{color:var(--ink)}
.tk-statusbar{display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding-top:4px; border-top:1px dashed var(--line)}
.tk-empty{text-align:center; padding:52px 20px; color:var(--sub)}
.tk-empty__mark{width:52px; height:52px; border-radius:50%; background:#dcfce7; color:#15803d;
  display:grid; place-items:center; font-size:24px; margin:0 auto 14px}
.tk-forbidden{padding:24px; max-width:520px}
.tk-forbidden h3{color:#c0392b; margin:0 0 6px}
.tk-forbidden p{color:var(--sub); margin:0}

@media (max-width:680px){
  .tk-uploads{grid-template-columns:1fr}
  .tk-prio{grid-template-columns:1fr}
  .tk-table thead{display:none}
  .tk-table, .tk-table tbody, .tk-table tr, .tk-table td{display:block; width:100%}
  .tk-table tr{border-bottom:1px solid var(--line); padding:12px 14px}
  .tk-table td{border:0; padding:5px 0; display:flex; justify-content:space-between; gap:12px}
  .tk-table td::before{content:attr(data-label); font:600 11px 'DM Sans'; text-transform:uppercase;
    letter-spacing:.04em; color:var(--sub)}
  .tk-table td.tk-idcell::before{content:""}
}
@media (prefers-reduced-motion:reduce){ .tk-root *{transition:none!important} }
`;
